import * as yup from "yup";

import type { Currency } from "@krea/shared/types/common";

import { AmortizationMethodEnum, BidTypeEnum } from "@/features/common/enums";
import type { IRealEstateBidInput } from "@/features/lender-responses/types/lender-response";

import { AmountValidation } from "../utils/validators/AmountValidation";
import { LenderMonthlyAmountValidation } from "../utils/validators/LenderMonthlyAmountValidation";
import { LenderMonthlyInterestValidation } from "../utils/validators/LenderMonthlyInterestValidation";
import { PaybackTimeMonthsValidation } from "../utils/validators/PaybackTimeMonthsValidation";
import { SetupFeeValidation } from "../utils/validators/SetupFeeValidation";
import { TotalCostValidation } from "../utils/validators/TotalCostValidation";

export const realEstateBidSchema: (
  currency: Currency,
  formatWithCurrency: (value: number) => string,
) => yup.ObjectSchema<IRealEstateBidInput> = (currency, formatWithCurrency) =>
  yup.object().shape({
    type: yup
      .string()
      .required("Type is required")
      .oneOf([BidTypeEnum.REAL_ESTATE])
      .default(BidTypeEnum.REAL_ESTATE),
    amount: AmountValidation(currency, formatWithCurrency),
    paybackTimeMonths: PaybackTimeMonthsValidation(),
    amortizationMethod: yup
      .string()
      .oneOf(Object.values(AmortizationMethodEnum))
      .required("Amortization method is required"),
    lenderMonthlyInterest: LenderMonthlyInterestValidation({
      isFunction: (amortizationMethod: AmortizationMethodEnum) =>
        amortizationMethod !== AmortizationMethodEnum.FIXED,
    }),
    lenderMonthlyAmount: LenderMonthlyAmountValidation(
      currency,
      formatWithCurrency,
    ),
    totalCost: TotalCostValidation(formatWithCurrency),
    earlyRepaymentFee: yup.string().nullable(),
    postalFee: yup.number().nullable().min(0, "Should be at least 0"),
    directDebitFee: yup.number().nullable().min(0, "Should be at least 0"),
    setupFee: SetupFeeValidation(formatWithCurrency),
    amortizationInPercent: yup.number().when("amortizationMethod", {
      is: (amortizationMethod: AmortizationMethodEnum) =>
        amortizationMethod === AmortizationMethodEnum.LINEAR,
      then: (schema) =>
        schema
          .required("Amortization in % is required")
          .typeError("Amortization in % is required")
          .min(
            0,
            (schema) =>
              `Amortization in % needs to be greater than ${schema.min}`,
          )
          .max(
            100,
            (schema) =>
              `Amortization in % can't be greater than ${schema.max}%`,
          ),
      otherwise: (schema) => schema.nullable(),
    }),
  });

export const realEstateBidDefaultValues: IRealEstateBidInput = {
  type: BidTypeEnum.REAL_ESTATE,
  amount: undefined,
  paybackTimeMonths: undefined,
  amortizationMethod: AmortizationMethodEnum.ANNUITY,
  lenderMonthlyInterest: undefined,
  lenderMonthlyAmount: undefined,
  totalCost: undefined,
  earlyRepaymentFee: undefined,
  postalFee: undefined,
  directDebitFee: undefined,
  setupFee: undefined,
  amortizationInPercent: undefined,
};
